/**
 * client/quests.ts
 * Responsabilidad: Gestor de Misiones Diarias, Logros Permanentes y Economía Dual en el cliente.
 *
 * Persistencia Híbrida:
 * - Invitados / offline: Persiste en localStorage bajo la clave "alchem_quests_local".
 * - Usuarios autenticados: Sincroniza con /api/quests/* en Supabase/PostgreSQL.
 * - Fusión inteligente: Si un invitado inicia sesión, su progreso local se transfiere al servidor.
 * - Batching y debounce: Sincronización periódica cada 10s, al morir el jugador y en beforeunload.
 */

import type { QuestItem, QuestState } from "./types";
import {
    DAILY_QUESTS_POOL,
    PERMANENT_ACHIEVEMENTS,
    getDailyQuestIdsForDate,
    MAX_DAILY_AD_WATCHES,
    AD_WATCH_REWARD_CRYSTALS,
    AD_WATCH_REWARD_ORBS,
    type QuestDefinition,
} from "../src/shared/constants";
import { safeGetStorage, safeSetStorage } from "./utils";
import { showRewardedAd, isPokiReady } from "./poki";
import { getCosmeticPrice } from "./cosmetics";

const QUESTS_LOCAL_STORAGE_KEY = "alchem_quests_local";

interface LocalQuestCache {
    version: number;
    crystals: number;
    astralOrbs: number;
    lastDailyDate: string;
    lastLoginDate: string;
    loginStreak: number;
    adWatchesRemaining: number;
    lastAdWatchDate: string;
    elementsKilled: string[];
    unlockedCosmetics?: string[];
    dailyQuests: Record<string, { progress: number; claimed: boolean }>;
    achievements: Record<string, { progress: number; claimed: boolean }>;
}

export class QuestManager {
    private static instance: QuestManager;

    private state: QuestState = {
        crystals: 0,
        astralOrbs: 0,
        dailyQuests: [],
        achievements: [],
        msUntilReset: 0,
        msUntilWeeklyReset: 0,
        adWatchesRemaining: MAX_DAILY_AD_WATCHES,
        loginStreak: 1,
    };

    private elementsKilled: Set<string> = new Set();
    private unlockedCosmetics: Set<string> = new Set();
    private isDirty: boolean = false;
    private syncTimer: ReturnType<typeof setInterval> | null = null;
    private resetCountdownTimer: ReturnType<typeof setInterval> | null = null;
    private pendingProgress: Record<string, any> = {};
    private listeners: Array<() => void> = [];

    public onQuestCompleted?: (quest: QuestItem) => void;

    private constructor() {
        this.init();
    }

    public static getInstance(): QuestManager {
        if (!QuestManager.instance) {
            QuestManager.instance = new QuestManager();
        }
        return QuestManager.instance;
    }

    /**
     * Inicializa el estado local y arranca sincronización periódica.
     */
    public async init(): Promise<void> {
        this.loadFromLocal();
        this.checkDailyReset();

        // Si hay sesión activa con token, sincronizar con el servidor
        const token = this.getAuthToken();
        if (token) {
            await this.fetchServerState();
        }

        // Timer de debounce periódico de guardado / sincronización cada 10 segundos
        if (!this.syncTimer) {
            this.syncTimer = setInterval(() => {
                if (this.isDirty) {
                    void this.flush();
                }
            }, 10000);
        }

        // Timer para descontar msUntilReset y msUntilWeeklyReset cada segundo
        if (!this.resetCountdownTimer) {
            this.resetCountdownTimer = setInterval(() => {
                if (this.state.msUntilReset > 1000) {
                    this.state.msUntilReset -= 1000;
                } else {
                    this.state.msUntilReset = this.calculateMsUntilReset();
                    this.checkDailyReset();
                }

                if (typeof this.state.msUntilWeeklyReset === "number" && this.state.msUntilWeeklyReset > 1000) {
                    this.state.msUntilWeeklyReset -= 1000;
                } else {
                    this.state.msUntilWeeklyReset = this.calculateMsUntilWeeklyReset();
                }
            }, 1000);
        }

        // Escucha de eventos de combate y juego para misiones y logros
        window.addEventListener("alchem:potion_drank", (e: any) => {
            this.trackPotionDrank(e.detail?.count || 1);
        });
        window.addEventListener("alchem:boost", (e: any) => {
            if (e.detail?.durationSec) {
                this.trackBoostTime(e.detail.durationSec);
            }
        });
        window.addEventListener("alchem:event_survived", (e: any) => {
            this.trackEventSurvived(e.detail?.count || 1);
        });
        window.addEventListener("alchem:killstreak", (e: any) => {
            if (typeof e.detail?.streak === "number") {
                this.trackKillstreak(e.detail.streak);
            }
        });

        // Guardado inmediato al salir de la pestaña
        window.addEventListener("beforeunload", () => {
            void this.flush();
        });
    }

    private getAuthToken(): string | null {
        return safeGetStorage("alchem_auth_token") || safeGetStorage("colyseus-auth-token") || null;
    }

    private calculateMsUntilReset(): number {
        const now = new Date();
        const nextMidnight = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0));
        return Math.max(0, nextMidnight.getTime() - now.getTime());
    }

    private calculateMsUntilWeeklyReset(): number {
        const now = Date.now();
        const todayUtc = new Date();
        const dayOfWeek = todayUtc.getUTCDay(); // 0 = Dom, 1 = Lun...
        const daysUntilMonday = ((8 - dayOfWeek) % 7) || 7;
        const nextMondayUtc = new Date(Date.UTC(
            todayUtc.getUTCFullYear(),
            todayUtc.getUTCMonth(),
            todayUtc.getUTCDate() + daysUntilMonday,
            0, 0, 0, 0
        ));
        return Math.max(0, nextMondayUtc.getTime() - now);
    }

    private getTodayUtcString(): string {
        return new Date().toISOString().slice(0, 10);
    }

    /**
     * Carga el estado guardado de localStorage para invitados o fallback offline.
     */
    private loadFromLocal(): void {
        const raw = safeGetStorage(QUESTS_LOCAL_STORAGE_KEY);
        const today = this.getTodayUtcString();
        this.state.msUntilReset = this.calculateMsUntilReset();
        this.state.msUntilWeeklyReset = this.calculateMsUntilWeeklyReset();

        if (raw) {
            try {
                const parsed = JSON.parse(raw) as Partial<LocalQuestCache>;
                this.state.crystals = typeof parsed.crystals === "number" ? parsed.crystals : 0;
                this.state.astralOrbs = typeof parsed.astralOrbs === "number" ? parsed.astralOrbs : 0;
                this.state.loginStreak = typeof parsed.loginStreak === "number" ? parsed.loginStreak : 1;
                this.state.adWatchesRemaining =
                    typeof parsed.adWatchesRemaining === "number" && parsed.lastAdWatchDate === today
                        ? parsed.adWatchesRemaining
                        : MAX_DAILY_AD_WATCHES;

                if (Array.isArray(parsed.elementsKilled)) {
                    this.elementsKilled = new Set(parsed.elementsKilled);
                }

                if (Array.isArray(parsed.unlockedCosmetics)) {
                    this.unlockedCosmetics = new Set(parsed.unlockedCosmetics);
                } else {
                    this.unlockedCosmetics = new Set();
                }

                // Cargar o regenerar misiones diarias
                const lastDate = parsed.lastDailyDate;
                if (lastDate === today && parsed.dailyQuests) {
                    this.state.dailyQuests = Object.keys(parsed.dailyQuests)
                        .map((qid) => {
                            const def = DAILY_QUESTS_POOL.find((q) => q.id === qid);
                            if (!def) return null;
                            const prog = parsed.dailyQuests![qid];
                            return {
                                ...def,
                                progress: prog.progress,
                                claimed: prog.claimed,
                                isAchievement: false,
                            };
                        })
                        .filter((q): q is QuestItem => q !== null);
                } else {
                    this.generateDailyQuestsLocal(today);
                }

                // Cargar logros permanentes
                const achRecord = parsed.achievements || {};
                this.state.achievements = PERMANENT_ACHIEVEMENTS.map((ach) => {
                    const prog = achRecord[ach.id] || { progress: 0, claimed: false };
                    return {
                        ...ach,
                        progress: prog.progress,
                        claimed: prog.claimed,
                        isAchievement: true,
                    };
                });

                return;
            } catch {
                // Parse error fallback
            }
        }

        // Defaults para jugador nuevo
        this.generateDailyQuestsLocal(today);
        this.state.achievements = PERMANENT_ACHIEVEMENTS.map((ach) => ({
            ...ach,
            progress: 0,
            claimed: false,
            isAchievement: true,
        }));
        this.saveToLocal();
    }

    /**
     * Genera 3 misiones diarias locales con semilla determinística por fecha UTC.
     */
    private generateDailyQuestsLocal(today: string): void {
        const questIds = getDailyQuestIdsForDate(today);
        this.state.dailyQuests = questIds
            .map((qid) => {
                const def = DAILY_QUESTS_POOL.find((q) => q.id === qid);
                if (!def) return null;
                return {
                    ...def,
                    progress: 0,
                    claimed: false,
                    isAchievement: false,
                };
            })
            .filter((q): q is QuestItem => q !== null);
    }

    /**
     * Verifica si cambió el día UTC para renovar misiones y racha de logins.
     */
    public checkDailyReset(): void {
        const today = this.getTodayUtcString();
        const raw = safeGetStorage(QUESTS_LOCAL_STORAGE_KEY);
        let lastDailyDate = "";

        if (raw) {
            try {
                const parsed = JSON.parse(raw);
                lastDailyDate = parsed.lastDailyDate || "";
            } catch {
                // ignore
            }
        }

        if (lastDailyDate !== today) {
            // Actualizar racha de inicio de sesión
            if (lastDailyDate) {
                const lastDateObj = new Date(lastDailyDate + "T00:00:00Z");
                const todayDateObj = new Date(today + "T00:00:00Z");
                const diffDays = Math.round((todayDateObj.getTime() - lastDateObj.getTime()) / (1000 * 60 * 60 * 24));
                if (diffDays === 1) {
                    this.state.loginStreak += 1;
                } else if (diffDays > 1) {
                    this.state.loginStreak = 1;
                }
            } else {
                this.state.loginStreak = 1;
            }

            // Sincronizar logro login_streak_5
            const loginAch = this.state.achievements.find((a) => a.id === "login_streak_5");
            if (loginAch && !loginAch.claimed) {
                loginAch.progress = Math.min(loginAch.target, this.state.loginStreak);
            }

            this.state.adWatchesRemaining = MAX_DAILY_AD_WATCHES;
            this.generateDailyQuestsLocal(today);
            this.saveToLocal();
            this.notify();
        }
    }

    /**
     * Guarda el estado actual en localStorage.
     */
    private saveToLocal(): void {
        const today = this.getTodayUtcString();
        const dailyQuestsRecord: Record<string, { progress: number; claimed: boolean }> = {};
        for (const q of this.state.dailyQuests) {
            dailyQuestsRecord[q.id] = { progress: q.progress, claimed: q.claimed };
        }

        const achievementsRecord: Record<string, { progress: number; claimed: boolean }> = {};
        for (const a of this.state.achievements) {
            achievementsRecord[a.id] = { progress: a.progress, claimed: a.claimed };
        }

        const data: LocalQuestCache = {
            version: 1,
            crystals: this.state.crystals,
            astralOrbs: this.state.astralOrbs,
            lastDailyDate: today,
            lastLoginDate: today,
            loginStreak: this.state.loginStreak,
            adWatchesRemaining: this.state.adWatchesRemaining,
            lastAdWatchDate: today,
            elementsKilled: Array.from(this.elementsKilled),
            unlockedCosmetics: Array.from(this.unlockedCosmetics),
            dailyQuests: dailyQuestsRecord,
            achievements: achievementsRecord,
        };

        safeSetStorage(QUESTS_LOCAL_STORAGE_KEY, JSON.stringify(data));
    }

    /**
     * Obtiene el estado autoritativo desde el endpoint protegido /api/quests.
     */
    public async fetchServerState(): Promise<void> {
        const token = this.getAuthToken();
        if (!token) return;

        try {
            const res = await fetch("/api/quests", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!res.ok) return;

            const data = (await res.json()) as {
                crystals: number;
                astralOrbs: number;
                dailyQuests: QuestItem[];
                achievements: QuestItem[];
                msUntilReset: number;
                msUntilWeeklyReset?: number;
                adWatchesRemaining: number;
                loginStreak: number;
            };

            // Misiones diarias autoritativas del servidor (sin contaminar con local)
            if (Array.isArray(data.dailyQuests)) {
                this.state.dailyQuests = data.dailyQuests.map((q) => ({ ...q, isAchievement: false }));
            }

            // Logros permanentes autoritativos del servidor (sin contaminar con local)
            if (Array.isArray(data.achievements)) {
                this.state.achievements = data.achievements.map((a) => ({ ...a, isAchievement: true }));
            }

            this.state.crystals = data.crystals;
            this.state.astralOrbs = data.astralOrbs;
            this.state.msUntilReset = data.msUntilReset;
            this.state.msUntilWeeklyReset = data.msUntilWeeklyReset || this.calculateMsUntilWeeklyReset();
            this.state.adWatchesRemaining = data.adWatchesRemaining;
            this.state.loginStreak = data.loginStreak;

            // Limpiar pendientes locales para no empujar progreso espurio
            this.pendingProgress = {};
            this.isDirty = false;
            this.saveToLocal();
            this.notify();

            // Sincronizar cosméticos desbloqueados almacenados en Supabase
            if (Array.isArray((data as any).unlockedCosmetics)) {
                this.unlockedCosmetics = new Set((data as any).unlockedCosmetics);
                this.saveToLocal();
            } else {
                try {
                    const profileRes = await fetch("/api/profile", {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (profileRes.ok) {
                        const profileData = await profileRes.json();
                        const serverUnlocked = profileData?.unlockedCosmetics || profileData?.questData?.unlockedCosmetics;
                        if (Array.isArray(serverUnlocked)) {
                            this.unlockedCosmetics = new Set(serverUnlocked);
                            this.saveToLocal();
                        }
                    }
                } catch (pErr) {
                    console.warn("[Quests] No se pudo sincronizar cosméticos desde el perfil:", pErr);
                }
            }
        } catch (e) {
            console.warn("[Quests] No se pudo obtener el estado del servidor, usando local:", e);
        }
    }

    // =========================================================================
    // Seguimiento pasivo desacoplado de eventos de juego (Zero Canvas Impact)
    // =========================================================================

    /** Registra bajas de jugadores (kill normal o shutdown) */
    public trackKill(element: string, wasShutdown: boolean, streak?: number): void {
        this.addProgress("kill_players", 1);

        if (element) {
            this.elementsKilled.add(element);
            const achKillAll = this.state.achievements.find((a) => a.id === "kill_with_all_elements");
            if (achKillAll && !achKillAll.claimed) {
                achKillAll.progress = Math.min(achKillAll.target, this.elementsKilled.size);
                this.pendingProgress["element_kill"] = element;
                this.pendingProgress["kill_with_all_elements"] = this.elementsKilled.size;
                this.isDirty = true;
            }
        }

        if (wasShutdown) {
            this.addProgress("shutdown_bounty", 1);
        }

        if (typeof streak === "number" && streak > 0) {
            this.trackKillstreak(streak);
        }
    }

    /** Registra nutrientes / bolitas de comida consumidas */
    public trackFoodEaten(amount: number = 1): void {
        this.addProgress("eat_orbs", amount);
        this.addProgress("mass_glutton", amount);
    }

    /** Registra daño infligido con counters elementales */
    public trackCounterDamage(damage: number): void {
        const rounded = Math.round(damage);
        if (rounded > 0) {
            this.addProgress("drain_mass_counter", rounded);
            this.addProgress("elemental_nemesis", 1);
        }
    }

    /** Registra masa máxima alcanzada en la partida */
    public trackMass(currentMass: number): void {
        const mass = Math.round(currentMass);
        if (mass <= 0) return;

        // Misión diaria: reach_mass (2500)
        const dailyReach = this.state.dailyQuests.find((q) => q.type === "reach_mass");
        if (dailyReach && !dailyReach.claimed && mass > dailyReach.progress) {
            const prevProg = dailyReach.progress;
            dailyReach.progress = Math.min(dailyReach.target, mass);
            if (prevProg < dailyReach.target && dailyReach.progress >= dailyReach.target) {
                this.onQuestCompleted?.(dailyReach);
            }
            this.pendingProgress["reach_mass"] = mass;
            this.isDirty = true;
            this.saveToLocal();
            this.notify();
        }

        // Logro: reach_titan_8k (8000)
        const titanAch = this.state.achievements.find((a) => a.id === "reach_titan_8k");
        if (titanAch && !titanAch.claimed && mass > titanAch.progress) {
            const prevProg = titanAch.progress;
            titanAch.progress = Math.min(titanAch.target, mass);
            if (prevProg < titanAch.target && titanAch.progress >= titanAch.target) {
                this.onQuestCompleted?.(titanAch);
            }
            this.pendingProgress["reach_titan_8k"] = mass;
            this.isDirty = true;
            this.saveToLocal();
            this.notify();
        }
    }

    /** Registra tiempo de supervivencia en una sola vida (ms) */
    public trackSurvivalTime(lifeDurationMs: number): void {
        const duration = Math.round(lifeDurationMs);
        if (duration <= 0) return;

        const dailySurvive = this.state.dailyQuests.find((q) => q.type === "survive_time");
        if (dailySurvive && !dailySurvive.claimed && duration > dailySurvive.progress) {
            const prevProg = dailySurvive.progress;
            dailySurvive.progress = Math.min(dailySurvive.target, duration);
            if (prevProg < dailySurvive.target && dailySurvive.progress >= dailySurvive.target) {
                this.onQuestCompleted?.(dailySurvive);
            }
            this.pendingProgress["survive_time"] = duration;
            this.isDirty = true;
            this.saveToLocal();
            this.notify();
        }
    }

    /** Registra recolección de power-ups en la arena */
    public trackPowerUpCollected(): void {
        this.addProgress("collect_powerups", 1);
    }

    /** Registra pociones consumidas en combate */
    public trackPotionDrank(count: number = 1): void {
        this.addProgress("drink_potions", count);
    }

    /** Registra tiempo de uso de propulsor / turbo (segundos) */
    public trackBoostTime(seconds: number): void {
        const rounded = Math.round(seconds);
        if (rounded > 0) {
            this.addProgress("boost_time", rounded);
        }
    }

    /** Registra racha de bajas consecutivas */
    public trackKillstreak(streak: number): void {
        if (streak > 0) {
            this.addProgress("killstreak_god", streak);
        }
    }

    /** Registra supervivencia a eventos globales */
    public trackEventSurvived(count: number = 1): void {
        this.addProgress("event_survivor", count);
    }

    /** Registra acumulación de masa */
    public trackMassAccumulated(mass: number): void {
        const rounded = Math.round(mass);
        if (rounded > 0) {
            this.addProgress("mass_glutton", rounded);
        }
    }

    /** Registra aplicación de debilidades / counters elementales */
    public trackElementalCounter(count: number = 1): void {
        this.addProgress("elemental_nemesis", count);
    }

    /** Incremento genérico acumulativo */
    public addProgress(questTypeOrId: string, amount: number): void {
        if (amount <= 0) return;
        let changed = false;

        // Revisar misiones diarias
        for (const quest of this.state.dailyQuests) {
            if ((quest.id === questTypeOrId || quest.type === questTypeOrId) && !quest.claimed) {
                const prev = quest.progress;
                quest.progress = Math.min(quest.target, quest.progress + amount);
                if (quest.progress !== prev) {
                    changed = true;
                    if (prev < quest.target && quest.progress >= quest.target) {
                        this.onQuestCompleted?.(quest);
                    }
                }
            }
        }

        // Revisar logros semanales / permanentes
        for (const ach of this.state.achievements) {
            if ((ach.id === questTypeOrId || (ach.type as string) === questTypeOrId) && !ach.claimed) {
                const prev = ach.progress;
                if ((ach.type as string) === "killstreak_streak" || ach.id === "killstreak_god") {
                    ach.progress = Math.min(ach.target, Math.max(ach.progress, amount));
                } else {
                    ach.progress = Math.min(ach.target, ach.progress + amount);
                }
                if (ach.progress !== prev) {
                    changed = true;
                    if (prev < ach.target && ach.progress >= ach.target) {
                        this.onQuestCompleted?.(ach);
                    }
                }
            }
        }

        if (changed) {
            if (questTypeOrId === "killstreak_god" || questTypeOrId === "killstreak_streak") {
                const currentPending = typeof this.pendingProgress[questTypeOrId] === "number" ? this.pendingProgress[questTypeOrId] : 0;
                this.pendingProgress[questTypeOrId] = Math.max(currentPending, amount);
            } else {
                const currentPending = typeof this.pendingProgress[questTypeOrId] === "number" ? this.pendingProgress[questTypeOrId] : 0;
                this.pendingProgress[questTypeOrId] = currentPending + amount;
            }
            this.isDirty = true;
            this.saveToLocal();
            this.notify();
        }
    }

    // =========================================================================
    // Sincronización al servidor (Debounced / Flush)
    // =========================================================================

    public async flush(): Promise<void> {
        this.saveToLocal();
        const token = this.getAuthToken();
        if (!token || !this.isDirty || Object.keys(this.pendingProgress).length === 0) {
            this.isDirty = false;
            return;
        }

        const payload = { ...this.pendingProgress };
        this.pendingProgress = {};
        this.isDirty = false;

        try {
            const res = await fetch("/api/quests/progress", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (res.ok) {
                const data = (await res.json()) as Partial<QuestState>;
                if (typeof data.crystals === "number") this.state.crystals = data.crystals;
                if (typeof data.astralOrbs === "number") this.state.astralOrbs = data.astralOrbs;
                this.saveToLocal();
            } else {
                // Re-encolar si falló la petición de red
                this.pendingProgress = { ...payload, ...this.pendingProgress };
                this.isDirty = true;
            }
        } catch (e) {
            console.warn("[Quests] Error al enviar progreso al servidor:", e);
            this.pendingProgress = { ...payload, ...this.pendingProgress };
            this.isDirty = true;
        }
    }

    // =========================================================================
    // Reclamo de recompensas y Canalización Astral (Anuncios)
    // =========================================================================

    /**
     * Reclama la recompensa de una misión diaria o logro completado.
     */
    public async claimReward(questId: string): Promise<boolean> {
        const quest =
            this.state.dailyQuests.find((q) => q.id === questId) ||
            this.state.achievements.find((a) => a.id === questId);

        if (!quest || quest.claimed || quest.progress < quest.target) {
            return false;
        }

        const token = this.getAuthToken();
        if (token) {
            try {
                // Asegurar que el servidor tenga el último progreso antes de reclamar
                await this.flush();

                const res = await fetch("/api/quests/claim", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ questId }),
                });

                if (res.ok) {
                    const data = (await res.json()) as { crystals: number; astralOrbs: number };
                    quest.claimed = true;
                    this.state.crystals = data.crystals;
                    this.state.astralOrbs = data.astralOrbs;
                    this.saveToLocal();
                    this.notify();
                    return true;
                } else {
                    console.warn("[Quests] Error al reclamar recompensa en servidor:", res.status, await res.text().catch(() => ""));
                    return false;
                }
            } catch (e) {
                console.error("[Quests] Error al reclamar recompensa en servidor:", e);
                return false;
            }
        }

        // Modo invitado (offline / local)
        quest.claimed = true;
        this.state.crystals += quest.rewardCrystals;
        this.state.astralOrbs += quest.rewardAstralOrbs;
        this.saveToLocal();
        this.notify();
        return true;
    }

    /**
     * Canalización Astral: Visualiza un anuncio remunerado para obtener +2 Orbes Astrales y +50 Cristales.
     * Máximo 3 anuncios diarios.
     */
    public async watchAd(): Promise<{ success: boolean; error?: string }> {
        if (this.state.adWatchesRemaining <= 0) {
            return { success: false, error: "daily_limit_reached" };
        }

        // Mostrar anuncio mediante el SDK de Poki si está disponible, o simulación
        let adSuccess = false;
        if (isPokiReady()) {
            adSuccess = await showRewardedAd();
        } else {
            // Simulación transparente de visualización de anuncio (fallback en dev)
            await new Promise((resolve) => setTimeout(resolve, 800));
            adSuccess = true;
        }

        if (!adSuccess) {
            return { success: false, error: "ad_skipped" };
        }

        const token = this.getAuthToken();
        if (token) {
            try {
                const res = await fetch("/api/quests/watch-ad", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({}),
                });

                if (res.ok) {
                    const data = (await res.json()) as {
                        crystals: number;
                        astralOrbs: number;
                        adWatchesRemaining: number;
                    };
                    this.state.crystals = data.crystals;
                    this.state.astralOrbs = data.astralOrbs;
                    this.state.adWatchesRemaining = data.adWatchesRemaining;
                    this.saveToLocal();
                    this.notify();
                    return { success: true };
                }
            } catch (e) {
                console.error("[Quests] Error al acreditar anuncio en el servidor:", e);
            }
        }

        // Modo invitado / local
        this.state.adWatchesRemaining = Math.max(0, this.state.adWatchesRemaining - 1);
        this.state.crystals += AD_WATCH_REWARD_CRYSTALS;
        this.state.astralOrbs += AD_WATCH_REWARD_ORBS;
        this.saveToLocal();
        this.notify();
        return { success: true };
    }

    // =========================================================================
    // Tienda de Cosméticos Bloqueados con Economía Dual
    // =========================================================================

    /**
     * Consulta si un cosmético está desbloqueado para el jugador.
     * Los cosméticos con precio 0 (todos los ojos y la opción 'none') son siempre gratuitos.
     */
    public hasUnlockedCosmetic(category: string, id: string): boolean {
        const priceInfo = getCosmeticPrice(category, id);
        if (priceInfo.price === 0) return true;
        return this.unlockedCosmetics.has(`${category}:${id}`);
    }

    /**
     * Desbloquea un cosmético mediante validación autoritativa en el backend (Supabase).
     * Requiere sesión activa; los invitados no pueden adquirir cosméticos permanentes.
     */
    public async unlockCosmetic(
        category: string,
        id: string,
        price: number,
        currency: "crystals" | "orbs"
    ): Promise<{ success: boolean; requireLogin?: boolean; error?: string }> {
        const token = this.getAuthToken();
        if (!token) {
            return { success: false, requireLogin: true };
        }

        const key = `${category}:${id}`;
        if (this.hasUnlockedCosmetic(category, id)) {
            return { success: true };
        }

        try {
            const res = await fetch("/api/shop/buy", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ category, itemId: id, currency }),
            });

            const data = await res.json();
            if (!res.ok || !data.success) {
                return { success: false, error: data?.error || "purchase_failed" };
            }

            if (typeof data.crystals === "number") {
                this.state.crystals = data.crystals;
            }
            if (typeof data.astralOrbs === "number") {
                this.state.astralOrbs = data.astralOrbs;
            }
            if (Array.isArray(data.unlockedCosmetics)) {
                this.unlockedCosmetics = new Set(data.unlockedCosmetics);
            } else {
                this.unlockedCosmetics.add(key);
            }

            this.saveToLocal();
            this.notify();
            return { success: true };
        } catch (e) {
            console.error("[Quests] Error al invocar /api/shop/buy:", e);
            return { success: false, error: "network_error" };
        }
    }

    /**
     * Sincroniza directamente saldos y cosméticos desbloqueados (usado tras Ruleta o eventos).
     */
    public setBalances(crystals: number, astralOrbs: number, unlocked?: string[]): void {
        this.state.crystals = crystals;
        this.state.astralOrbs = astralOrbs;
        if (Array.isArray(unlocked)) {
            for (const item of unlocked) {
                this.unlockedCosmetics.add(item);
            }
        }
        this.saveToLocal();
        this.notify();
    }

    /**
     * Restablece el gestor al estado limpio de invitado (sin residuos de cuentas anteriores).
     */
    public resetToGuest(): void {
        this.state.crystals = 0;
        this.state.astralOrbs = 0;
        this.state.loginStreak = 1;
        this.state.adWatchesRemaining = MAX_DAILY_AD_WATCHES;
        this.elementsKilled.clear();
        this.pendingProgress = {};
        this.isDirty = false;
        // Regenerar misiones diarias limpias con progreso 0 y claimed false
        const today = this.getTodayUtcString();
        this.generateDailyQuestsLocal(today);
        // Resetear logros permanentes limpios con progreso 0 y claimed false
        this.state.achievements = PERMANENT_ACHIEVEMENTS.map((ach) => ({
            ...ach,
            progress: 0,
            claimed: false,
            isAchievement: true,
        }));
        // Cosméticos base por defecto
        this.unlockedCosmetics = new Set([
            "eyes:default", "eyes:anime", "eyes:sharingan", "eyes:starry", "eyes:cyclops",
            "mouth:none", "glasses:none", "tattoo:none", "aura:none"
        ]);
        // Eliminar la clave local de quests para que no persista nada del usuario anterior
        try {
            localStorage.removeItem(QUESTS_LOCAL_STORAGE_KEY);
        } catch {}
        this.notify();
    }

    // =========================================================================
    // Getters y Suscripciones para UI
    // =========================================================================

    public getState(): Readonly<QuestState> {
        return this.state;
    }

    public getCrystals(): number {
        return this.state.crystals;
    }

    public getAstralOrbs(): number {
        return this.state.astralOrbs;
    }

    public updateBalances(crystals?: number, astralOrbs?: number): void {
        let changed = false;
        if (typeof crystals === "number") {
            this.state.crystals = crystals;
            changed = true;
        }
        if (typeof astralOrbs === "number") {
            this.state.astralOrbs = astralOrbs;
            changed = true;
        }
        if (changed) {
            this.saveToLocal();
            this.notify();
        }
    }

    public getClaimableCount(): number {
        let count = 0;
        for (const q of this.state.dailyQuests) {
            if (!q.claimed && q.progress >= q.target) count++;
        }
        for (const a of this.state.achievements) {
            if (!a.claimed && a.progress >= a.target) count++;
        }
        return count;
    }

    public hasClaimableRewards(): boolean {
        return this.getClaimableCount() > 0;
    }

    public subscribe(fn: () => void): () => void {
        this.listeners.push(fn);
        return () => {
            this.listeners = this.listeners.filter((l) => l !== fn);
        };
    }

    private notify(): void {
        for (const listener of this.listeners) {
            try {
                listener();
            } catch (err) {
                console.warn("[QuestManager] Error en listener:", err);
            }
        }
    }
}

export const questManager = QuestManager.getInstance();
